import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { SearchRounded as SearchRoundedIcon } from "../icons/FigmaControlIcons";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const BLUE = "#3451F4";
const PURPLE = "#9C4FFF";
const PURPLE_DARK = "#7C3AED";
const BORDER = "#E4E7EC";
const MUTED = "#98A2B3";
const TEXT = "#344054";

function safeText(value, fallback = "") {
  if (value === undefined || value === null) return fallback;
  const normalized = String(value).trim();
  return normalized || fallback;
}

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    ""
  );
}

function normalizeLookupValue(value) {
  return safeText(value).toLowerCase();
}

function buildJobLookupOption(raw = {}) {
  const safeRaw = raw && typeof raw === "object" ? raw : {};
  const jobId = Number(safeRaw.job_id ?? safeRaw.jobId ?? safeRaw.id ?? 0);
  const companyId = Number(safeRaw.company_id ?? safeRaw.companyId ?? 0);
  const jobTitle = safeText(
    safeRaw.job_title ?? safeRaw.jobTitle ?? safeRaw.title ?? safeRaw.name
  );
  if (!jobTitle) return null;

  return {
    id: Number.isFinite(jobId) && jobId > 0 ? jobId : null,
    job_title: jobTitle,
    company_id: Number.isFinite(companyId) && companyId > 0 ? companyId : null,
    company_name: safeText(
      safeRaw.company_name ?? safeRaw.companyName ?? safeRaw.company
    ),
    company_logo: safeText(
      safeRaw.company_logo ?? safeRaw.companyLogo ?? safeRaw.logo
    ),
    location: safeText(safeRaw.location),
    status: safeText(safeRaw.status),
    source: safeText(safeRaw.source),
    owner: safeText(
      safeRaw.owner ?? safeRaw.owner_name ?? safeRaw.ownerName
    ),
  };
}

function formatJobLookupLabel(option = {}) {
  const jobTitle = safeText(option?.job_title);
  const companyName = safeText(option?.company_name);

  if (jobTitle && companyName) {
    return `${jobTitle} - ${companyName}`;
  }

  return jobTitle || companyName || "";
}

function getJobLookupKey(option = {}) {
  if (option?.id != null) return `job:${option.id}`;
  return `job-title:${normalizeLookupValue(option?.job_title)}::company:${normalizeLookupValue(
    option?.company_name
  )}`;
}

function mergeJobLookupOptions(selectedJob, rows = []) {
  const merged = new Map();

  [selectedJob, ...(Array.isArray(rows) ? rows : [])]
    .filter(Boolean)
    .forEach((option) => {
      const key = safeText(getJobLookupKey(option));
      if (!key) return;
      merged.set(key, {
        ...(merged.get(key) || {}),
        ...option,
      });
    });

  return Array.from(merged.values());
}

function normalizeStageOptions(options = []) {
  return (Array.isArray(options) ? options : [])
    .map((option) => {
      if (typeof option === "string") {
        const value = safeText(option);
        return value ? { value, label: value } : null;
      }

      if (!option || typeof option !== "object") return null;

      const value = safeText(option.value ?? option.label);
      const label = safeText(option.label ?? option.value);
      return value && label ? { value, label } : null;
    })
    .filter(Boolean);
}

export default function AddCandidateToJobDialog({
  open,
  onClose,
  onSubmit,
  stageOptions = [],
  initialStage = "",
  candidateName = "",
}) {
  const normalizedStageOptions = useMemo(
    () => normalizeStageOptions(stageOptions),
    [stageOptions]
  );
  const defaultStage = useMemo(() => {
    const requestedStage = safeText(initialStage);
    if (
      requestedStage &&
      normalizedStageOptions.some((option) => option.value === requestedStage)
    ) {
      return requestedStage;
    }

    return normalizedStageOptions[0]?.value || "";
  }, [initialStage, normalizedStageOptions]);

  const [selectedJob, setSelectedJob] = useState(null);
  const [jobInputValue, setJobInputValue] = useState("");
  const [jobLookupOptions, setJobLookupOptions] = useState([]);
  const [jobLookupLoading, setJobLookupLoading] = useState(false);
  const [selectedStage, setSelectedStage] = useState(defaultStage);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;

    setSelectedJob(null);
    setJobInputValue("");
    setJobLookupOptions([]);
    setSelectedStage(defaultStage);
    setSubmitting(false);
    setError("");
  }, [defaultStage, open]);

  useEffect(() => {
    if (!open) return;

    let active = true;
    const query = safeText(jobInputValue);
    const timerId = window.setTimeout(async () => {
      setJobLookupLoading(true);
      try {
        const params = new URLSearchParams({
          page: "1",
          limit: "25",
        });
        if (query) params.set("search", query);

        const token = getToken();
        const response = await fetch(`${API_BASE}/api/jobs?${params.toString()}`, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(token ? { "x-access-token": token } : {}),
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load jobs (${response.status})`);
        }

        const payload = await response.json().catch(() => ({}));
        const rows = (Array.isArray(payload) ? payload : payload?.rows || [])
          .map((item) => buildJobLookupOption(item))
          .filter(Boolean);

        if (!active) return;
        setJobLookupOptions(mergeJobLookupOptions(selectedJob, rows));
      } catch (nextError) {
        if (!active) return;
        console.error("Failed to load jobs for add-to-job dialog:", nextError);
        setJobLookupOptions(mergeJobLookupOptions(selectedJob, []));
      } finally {
        if (active) {
          setJobLookupLoading(false);
        }
      }
    }, query ? 250 : 0);

    return () => {
      active = false;
      window.clearTimeout(timerId);
    };
  }, [jobInputValue, open, selectedJob]);

  const handleClose = useCallback(() => {
    if (!submitting) {
      onClose?.();
    }
  }, [onClose, submitting]);

  const handleSubmit = useCallback(async () => {
    if (!selectedJob?.id) {
      setError("Select a job title before continuing.");
      return;
    }

    if (!safeText(selectedStage)) {
      setError("Select a stage before adding the candidate to the job.");
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      await onSubmit?.(selectedJob, { candidateStage: selectedStage });
      onClose?.();
    } catch (submitError) {
      setError(submitError?.message || "Failed to add candidate to the selected job.");
    } finally {
      setSubmitting(false);
    }
  }, [onClose, onSubmit, selectedJob, selectedStage]);

  const selectedJobMeta = useMemo(
    () =>
      [selectedJob?.location, selectedJob?.status]
        .filter(Boolean)
        .join(" | "),
    [selectedJob]
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: { xs: "calc(100% - 24px)", sm: 620 },
          maxWidth: "620px",
          borderRadius: 4,
          bgcolor: "#FFFFFF",
          boxShadow: "0 28px 80px rgba(16, 24, 40, 0.18)",
          overflow: "hidden",
        },
      }}
    >
      <Box sx={{ px: { xs: 2.25, sm: 3.5 }, py: { xs: 2.25, sm: 3 } }}>
        <Stack data-popup-header direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2.5,
                display: "grid",
                placeItems: "center",
                bgcolor: "rgba(52,81,244,0.08)",
                color: BLUE,
              }}
            >
              <WorkOutlineRoundedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography data-popup-title sx={{ fontSize: 19, fontWeight: 800, color: "#101828" }}>
              Add to Job
            </Typography>
          </Stack>

          <IconButton data-popup-close
            onClick={handleClose}
            disabled={submitting}
            sx={{
              width: 36,
              height: 36,
              bgcolor: "rgba(156,79,255,0.12)",
              color: PURPLE_DARK,
              "&:hover": {
                bgcolor: "rgba(156,79,255,0.18)",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 19 }} />
          </IconButton>
        </Stack>

        <Typography sx={{ mt: 2, color: "#667085", fontSize: 15, lineHeight: 1.6 }}>
          {candidateName
            ? `Select an existing job and stage to assign ${candidateName} to that role.`
            : "Select an existing job and stage to assign this candidate to that role."}
        </Typography>

        <Box
          sx={{
            mt: 2.2,
            border: `1px solid ${BORDER}`,
            borderRadius: 3,
            p: { xs: 2, sm: 2.25 },
            bgcolor: "#FFFFFF",
          }}
        >
          <Stack spacing={2}>
            {error ? <Alert severity="error">{error}</Alert> : null}

            <Box>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: TEXT, mb: 0.8 }}>
                Title
              </Typography>
              <Autocomplete
                options={jobLookupOptions}
                filterOptions={(options) => options}
                loading={jobLookupLoading}
                value={selectedJob}
                inputValue={jobInputValue}
                onInputChange={(_, nextValue, reason) => {
                  setJobInputValue(nextValue);
                  if (reason === "clear") {
                    setSelectedJob(null);
                  }
                }}
                onChange={(_, nextValue) => {
                  setSelectedJob(nextValue || null);
                  setJobInputValue(formatJobLookupLabel(nextValue));
                  setError("");
                }}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : formatJobLookupLabel(option)
                }
                isOptionEqualToValue={(option, value) =>
                  getJobLookupKey(option) === getJobLookupKey(value)
                }
                noOptionsText={jobLookupLoading ? "Loading..." : "No jobs found"}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    sx={{ display: "flex", alignItems: "center", gap: 1.1 }}
                  >
                    <Avatar
                      src={option.company_logo || undefined}
                      sx={{
                        width: 30,
                        height: 30,
                        fontSize: 11,
                        bgcolor: "#EEF2FF",
                        color: BLUE,
                      }}
                    >
                      {option.job_title?.charAt(0)?.toUpperCase() || "J"}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }} noWrap>
                        {option.job_title}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: MUTED }} noWrap>
                        {[option.company_name, option.location]
                          .filter(Boolean)
                          .join(" | ") || "Company"}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search job title or company"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchRoundedIcon sx={{ color: PURPLE, fontSize: 20 }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {jobLookupLoading ? <CircularProgress size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 999,
                        minHeight: 48,
                        bgcolor: "#FFFFFF",
                        "& fieldset": { borderColor: "#D6BBFB" },
                        "&:hover fieldset": { borderColor: "#C084FC" },
                        "&.Mui-focused fieldset": {
                          borderColor: PURPLE,
                          borderWidth: 2,
                        },
                      },
                    }}
                  />
                )}
              />
              {selectedJobMeta ? (
                <Typography sx={{ mt: 0.9, fontSize: 11, color: MUTED }}>
                  {selectedJobMeta}
                </Typography>
              ) : null}
            </Box>

            <Box>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: TEXT, mb: 0.8 }}>
                Stage
              </Typography>
              <TextField
                select
                fullWidth
                size="small"
                value={selectedStage}
                onChange={(event) => {
                  setSelectedStage(event.target.value);
                  setError("");
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2.5,
                    minHeight: 48,
                    bgcolor: "#FFFFFF",
                  },
                }}
              >
                {normalizedStageOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Stack>
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1.5}
          sx={{ mt: 2.75 }}
        >
          <Button
            onClick={handleClose}
            disabled={submitting}
            sx={{
              minWidth: 108,
              height: 44,
              borderRadius: "999px",
              border: "1px solid #B2DDFF",
              color: BLUE,
              fontWeight: 700,
              textTransform: "none",
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleSubmit}
            disabled={submitting || !selectedJob?.id}
            sx={{
              minWidth: 144,
              height: 44,
              borderRadius: "999px",
              textTransform: "none",
              fontWeight: 800,
              color: "#FFFFFF",
              background: "linear-gradient(90deg, #B655FF 0%, #8E4CFF 100%)",
              boxShadow: "none",
              "&:hover": {
                background: "linear-gradient(90deg, #A847FF 0%, #7A3FFF 100%)",
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                background: "#EAECF0",
                color: "#98A2B3",
              },
            }}
          >
            {submitting ? <CircularProgress size={18} sx={{ color: "#FFFFFF" }} /> : "Add to Job"}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
}
